// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const { CircleButton } = require('client-v2/common/components/buttons')
const UserAvatar = require('client-v2/common/components/user-avatar')
// Sibling imports
const useKeyboardSelection = require('./hooks/keyboard-selection')

const bem = BemEntity('SearchForm')

function SearchForm(props) {
  const [ isSuggestionsVisible, setSuggestionsVisible ] = React.useState(false)

  const showSuggestions = React.useCallback(() => {
    setSuggestionsVisible(true)
  }, [setSuggestionsVisible])

  const hideSuggestions = React.useCallback(() => {
    setSuggestionsVisible(false)
  }, [setSuggestionsVisible])

  /**
   * The input blur event pre-empts the suggestion click event;
   * delay hiding suggestions so that the click properly registers.
   */
  const hideSuggestionsAfterDelay = React.useCallback(() => {
    setTimeout(() => hideSuggestions(), 250)
  }, [hideSuggestions])

  const onInputChange = React.useCallback(event => {
    showSuggestions()
    props.onChange(event.target.value)
  }, [props.onChange, showSuggestions])

  const onReset = React.useCallback(event => {
    event.preventDefault()
    hideSuggestions()
    props.onReset()
  }, [props.onReset])

  const onSubmit = React.useCallback(event => {
    event.preventDefault()
    hideSuggestions()
    props.onSubmit()
  }, [props.onSubmit])

  const onSuggestionIndexSelect = React.useCallback(index => {
    props.onSubmit(props.suggestions[index])
  }, [props.suggestions, props.onSubmit])

  const inputRef = React.useRef()
  React.useEffect(() => {
    if (props.isVisible) {
      inputRef.current.focus()
    }
  }, [props.isVisible])

  if (!props.isVisible) {
    return null
  }
  return (
    tag('form', {
      className: classNames(
        props.className,
        bem.block()
      ),
      onReset,
      onSubmit
    },
      tag(CircleButton, {
        className: bem.element('button', 'submit'),
        type: 'submit'
      }),
      tag('input', {
        ref: inputRef,
        className: bem.element('form-input'),
        placeholder: 'Find people, collections and pages',
        value: props.value,
        onBlur: hideSuggestionsAfterDelay,
        onChange: onInputChange,
        onFocus: showSuggestions
      }),
      tag(CircleButton, {
        className: bem.element('button', 'reset'),
        type: 'reset'
      }),
      isSuggestionsVisible && (
        tag(Suggestions, {
          items: props.suggestions,
          onDismiss: hideSuggestions,
          onIndexSelect: onSuggestionIndexSelect
        })
      )
    )
  )
}

SearchForm.propTypes = {
  // Flags
  isVisible: PropTypes.bool.isRequired,
  // Values
  className: PropTypes.string,
  suggestions: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.string,
  // Callbacks
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
}

function Suggestions(props) {
  const itemCount = props.items.length
  const [ activeIndex, setActiveIndex ] = React.useState(null)

  React.useEffect(() => {
    if (activeIndex !== null && itemCount) {
      // Clamp activeIndex within items
      const clampedIndex = Math.max(0, Math.min(itemCount - 1, activeIndex))
      setActiveIndex(clampedIndex)
    } else {
      setActiveIndex(null)
    }
  }, [itemCount, activeIndex, setActiveIndex])

  const onItemHover = React.useCallback(event => {
    const { index } = event.currentTarget.dataset
    setActiveIndex(+index)
  }, [setActiveIndex])

  const onItemSelect = React.useCallback(event => {
    const { index } = event.currentTarget.dataset
    props.onIndexSelect(+index)
  }, [props.onIndexSelect])

  const ref = useKeyboardSelection({
    activeIndex,
    rowCount: itemCount,
    onDismiss: props.onDismiss,
    onIndexFocus: setActiveIndex,
    onIndexSelect: props.onIndexSelect
  })

  if (!itemCount) {
    return null
  }
  return (
    tag('div', {
      ref,
      className: bem.element('suggestions')
    },
      tag('ul', {
        className: bem.element('suggestions-list')
      },
        props.items.map((suggestion, index) => (
          tag('li', {
            key: `suggestion:${index}`,
            dataset: { index },
            onClick: onItemSelect,
            onMouseEnter: onItemHover
          },
            tag(Suggestion, {
              ...suggestion,
              isActive: index === activeIndex
            }))
          )
        )
      )
    )
  )
}

function Suggestion(props) {
  switch (props.type) {
    case 'contact': return UserSuggestion(props)
    default: return SearchSuggestion(props)
  }
}

function SearchSuggestion(props) {
  return (
    tag('div', {
      className: bem.element('suggestion', {
        active: props.isActive
      })
    },
      props.names[0]
    )
  )
}

function UserSuggestion(props) {
  return (
    tag('div', {
      className: bem.element('suggestion', {
        active: props.isActive
      })
    },
      tag(UserAvatar, {
        className: bem.element('suggestion-photo'),
        user: {
          'photo_url': props.photo,
          username: props.complete
        }
      }),
      tag('span', {
        className: bem.element('suggestion-name')
      },
        props.names[0]
      ),
      tag('span', {
        className: bem.element('suggestion-username')
      },
        props.names[1]
      )
    )
  )
}

module.exports = SearchForm
